import { Component } from "react";
import time from "src/utils/time";
import { Flex, Box } from "@chakra-ui/react";
import { VideoErrorType } from "src/modules/parent-station/lib/createVideoErrorManager";
import ControlButtons from "src/modules/parent-station/components/ControlButtons";
import VideoModeButton from "src/modules/detach-mode/components/VideoModeButton";
import UndetachButton from "src/modules/detach-mode/components/UndetachButton";
import LiveActivityLog from "src/modules/activity-log/LiveActivityLog";
import FadeOut from "src/modules/detach-mode/components/FadeOut";
import LullabiesFlow from "src/modules/lullaby/LullabiesFlow";
import { withFrame } from "src/components/Frame/Context";
import CameraPicker from "src/components/CameraPicker";
import PopoverView from "src/components/PopoverView";

import MonitoringStatusBar from "../../../components/MonitoringStatusBar";
import EveryNoise from "../../../components/EveryNoise";
import Settings from "../../../components/Settings";
import FlashlightIntensitySetter from "./components/FlashlightIntensitySetter";
import VideoIndicator from "./VideoMode/components/VideoIndicator";
import PremiumBanner from "./components/PremiumBanner";
import LowBatteryDialog from "./components/LowBatteryDialog";
import ErrorMode from "./ErrorMode";
import AudioMode from "./AudioMode";
import VideoMode from "./VideoMode";

const UPDATE_LAST_NOISE_BEFORE_PERIOD = 1000 * 20;
const UPDATE_LAST_MOTION_BEFORE_PERIOD = 1000 * 20;

function getLastNoiseOrMotionBefore(elapsedTime) {
  return elapsedTime ? time().diff(elapsedTime) : null;
}
class Monitor extends Component {
  state = {
    lastNoiseBefore: null,
    lastMotionBefore: null
  };

  static getDerivedStateFromProps(props, state) {
    return {
      lastNoiseBefore: getLastNoiseOrMotionBefore(props.babyStation.lastNoise),
      lastMotionBefore: getLastNoiseOrMotionBefore(props.babyStation.lastMotion)
    };
  }

  componentDidMount = () => {
    this.lastNoiseIntervalId = setInterval(this.updateLastNoiseBefore, UPDATE_LAST_NOISE_BEFORE_PERIOD);
    this.lastMotionIntervalId = setInterval(this.updateLastMotionBefore, UPDATE_LAST_MOTION_BEFORE_PERIOD);
    this.updateLastNoiseBefore();
    this.updateLastMotionBefore();
  };

  componentWillUnmount = async () => {
    clearInterval(this.lastNoiseIntervalId);
    clearInterval(this.lastMotionIntervalId);
  };

  updateLastNoiseBefore = () => {
    const { babyStation } = this.props;
    const lastNoiseBefore = getLastNoiseOrMotionBefore(babyStation.lastNoise);
    this.setState({ lastNoiseBefore });
  };

  updateLastMotionBefore = () => {
    const { babyStation } = this.props;
    const lastMotionBefore = getLastNoiseOrMotionBefore(babyStation.lastMotion);
    this.setState({ lastMotionBefore });
  };

  render() {
    const { lastNoiseBefore, lastMotionBefore } = this.state;
    const {
      changeCamera,
      togglePurchaseFlow,
      alert,
      renderLowBatteryDialog,
      toggleLowBatteryDialog,
      lowBatteryDialogProps,
      isInDetachMode,
      toggleMonitoringMode,
      avatarSize,
      isInTileView,
      openedFlow,
      isFading,
      toggleActivityLog,
      renderCameraList,
      closeCameraList,
      isActivityLogOpened,
      renderDetachButton,
      renderVideoButton,
      undetachWindow,
      openLullabyPlayer,
      closeFlow,
      babyStation,
      lullabyPlayer,
      detailNameInDialogs,
      muted,
      setAnimationControls,
      showSmallAvatar,
      avatarNamePosition,
      isFreeVersion
    } = this.props;

    const {
      remoteSettings,
      error,
      videoError,
      cameraAndFlashlightState,
      group,
      mode,
      activity,
      monitoringStartTime,
      batteryStatus,
      noiseMeter,
      senderStationConfiguration,
      motionDetectorState,
      jid,
      avatarId,
      isMultiRoom
    } = babyStation;
    const canDetectMotion = ["NOT_RUNNING", "UNKNOWN", "RUNNING"].includes(motionDetectorState);
    const monitoringStatusBar =
      isInDetachMode || isInTileView ? null : (
        <MonitoringStatusBar
          activatableFeatures={{
            muted,
            playingLullaby: Boolean(lullabyPlayer && lullabyPlayer.status === lullabyPlayer.Status.PLAYING),
            recording: false
          }}
          canDetectMotion={canDetectMotion}
          motionDetectorState={motionDetectorState}
          lullabyPlayer={lullabyPlayer}
          openLullabyPlayer={openLullabyPlayer}
          monitoringStartTime={monitoringStartTime}
          batteryStatus={batteryStatus}
          mode={mode}
          activity={activity}
          error={error}
          lowLightDetected={cameraAndFlashlightState?.lowLightDetected}
          lowLightMode={cameraAndFlashlightState?.lowLightMode}
          isInTileView={isInTileView}
        />
      );

    return (
      <>
        {renderDetachButton && <UndetachButton mode={mode} activity={activity} onClick={undetachWindow} />}
        {renderVideoButton && (
          <VideoModeButton mode={mode} activity={activity} onClick={() => toggleMonitoringMode(jid)} />
        )}

        {isFading && <FadeOut />}
        {!isInTileView && (
          <PopoverView isOpen={Boolean(openedFlow)} onClose={closeFlow}>
            {openedFlow === Settings && (
              <Settings
                isMultiRoom={isMultiRoom}
                avatarId={avatarId}
                jid={jid}
                displayMyAvatarButton={group === "myDevices"}
                onClose={closeFlow}
                micSensitivity={remoteSettings.micSensitivity}
                motionSensitivity={remoteSettings.motionDetectionSensitivity}
                supportsMotionDetection={senderStationConfiguration.supportedFeatures.includes("MOTION_DETECTION")}
              />
            )}
            {openedFlow === EveryNoise && <EveryNoise onClose={closeFlow} />}
            {openedFlow === LullabiesFlow && (
              <LullabiesFlow lullabyPlayer={lullabyPlayer} initialStep="player" onClose={closeFlow} />
            )}
          </PopoverView>
        )}
        <LowBatteryDialog
          isOpen={renderLowBatteryDialog}
          playLowBatterySound={lowBatteryDialogProps.playAlert}
          onClose={toggleLowBatteryDialog}
          deviceName={detailNameInDialogs ? lowBatteryDialogProps?.deviceName : null}
          avatarId={lowBatteryDialogProps?.avatarId}
        />
        {!error && mode === "audio" && (
          <AudioMode
            isInDetachMode={isInDetachMode}
            monitoringStatusBar={monitoringStatusBar}
            noiseMeter={openedFlow === EveryNoise ? null : noiseMeter}
            lastNoiseBefore={lastNoiseBefore}
            lastMotionBefore={lastMotionBefore}
            toggleActivityLog={toggleActivityLog}
            activity={activity}
            avatarId={avatarId}
            isInTileView={isInTileView}
            showSmallAvatar={showSmallAvatar}
            avatarSize={avatarSize}
            jid={jid}
            setAnimationControls={setAnimationControls}
            isMultiRoom={isMultiRoom}
          />
        )}
        {!error && mode === "video" && (
          <VideoMode
            babyStation={babyStation}
            isInDetachMode={isInDetachMode}
            monitoringStatusBar={monitoringStatusBar}
            noiseMeter={openedFlow === EveryNoise ? null : noiseMeter}
            lastNoiseBefore={lastNoiseBefore}
            lastMotionBefore={lastMotionBefore}
            toggleActivityLog={toggleActivityLog}
            isInTileView={isInTileView}
            avatarId={avatarId}
            avatarNamePosition={avatarNamePosition}
          />
        )}
        {error && <ErrorMode isInDetachMode={isInDetachMode} error={error} monitoringStatusBar={monitoringStatusBar} />}

        {error == null && (
          <Flex
            flexDirection="column"
            alignItems="start"
            position="absolute"
            bottom="14px"
            {...{ [isInTileView ? "right" : "left"]: "14px" }}
          >
            {mode === "video" && <VideoIndicator videoError={videoError} />}
            {isFreeVersion &&
            mode !== "video" &&
            videoError !== VideoErrorType.NOT_INCOMMING &&
            !isInDetachMode &&
            !isInTileView ? (
              <Box mt="8px">
                <PremiumBanner mode={mode} activity={activity} onClick={togglePurchaseFlow} />
              </Box>
            ) : null}
          </Flex>
        )}

        {error == null && !isInDetachMode && !isInTileView && (
          <ControlButtons closeCameraList={closeCameraList} undetachWindow={undetachWindow} />
        )}
        {error == null && !isInDetachMode && !isInTileView && <FlashlightIntensitySetter />}
        {!isInTileView && (
          <CameraPicker
            handleCameraChange={changeCamera}
            isOpen={renderCameraList}
            onClose={closeCameraList}
            cameraList={senderStationConfiguration.availableCameras}
            selectedCameraId={cameraAndFlashlightState.cameraId || senderStationConfiguration.selectedCameraId}
            left={92}
            isParentStation
            alert={alert}
          />
        )}
        {!isInTileView && (
          <LiveActivityLog
            isOpen={isActivityLogOpened}
            onClose={toggleActivityLog}
            mode={mode}
            monitoringStartTime={monitoringStartTime}
            togglePurchaseFlow={togglePurchaseFlow}
          />
        )}
      </>
    );
  }
}

export default withFrame(Monitor);
